'use client';

/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import Text from '@atoms/Text/Text';
import Alert from '@molecules/Alert/Alert';
import Page from '@organisms/Page/Page';
import useCustomer from '@hooks/useCustomer';
import Config from '@config';
import config from '@config';
import PromotionOffersFragment from '@molecules/PromotionOffersFragment/PromotionOffersFragment';
import ErrorComponent from '@molecules/ErrorComponent/ErrorComponent';
import usePromotionPageRouting from '@hooks/usePromotionPageRouting';
import useCmsPage from '@hooks/useCmsPage';
import cmsLabels from '@constants/cmsLabels';
import PromotionTabs from '@molecules/PromotionTabs/PromotionTabs';
import useStore from '@hooks/useStore';
import useLocalStorage from '@hooks/useLocalStorage';
import Icon from '@atoms/Icon/Icon';
import EmptyBag from '@icons/EmptyBag.svg';
import useResponsive from '@hooks/useResponsive';
import PageLayout from '@organisms/PageLayout/PageLayout';
import ProductBeamSkeleton from '@molecules/ProductBeam/ProductBeam.skeleton';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import Spinner from '@molecules/Spinner/Spinner';
import { trackOfflineStoreState } from '@helpers/analyticsHelpers/trackStore';
import {
  IconWrapper,
  StyledClosedStoreAlert,
  StyledPromotionPageContent,
  StyledPromotionPageHeading,
} from './PromotionPage.styles';
import usePromotionOfferList from './usePromotionOfferList';
import useIsHydrated from '@hooks/useIsHydrated';
import type { AxfoodStoreInfoViewModel } from '@occ/api-client';

export const MAPPED_TABS = {
  online: 'ehandel' as PromotionTabType,
  offline: 'butik' as PromotionTabType,
};

const label = cmsLabels.PROMOTION;
const NO_STORE = {} as AxfoodStoreInfoViewModel;

interface Props {
  mode?: PromotionTabType;
}

const PromotionPageClient = ({ mode }: Props) => {
  const { store } = useStore();
  const [promotionStoreIdLocalStorage, setPromotionStoreIdLocalStorage] = useLocalStorage(
    config.LOCAL_STORAGE_SELECTORS.promotionStoreId,
    ''
  );
  const { isMobile } = useResponsive();
  const { customer } = useCustomer();
  const t = useTranslations('promotionPage');
  const { activeTab, selectedOfflineStore, setSelectedOfflineStore, shouldOpenFlyer } = usePromotionPageRouting(mode);
  // The selected store and the offers come from localStorage and client fetches, which the server doesn't have.
  // Render the same shell as the server until hydration is done, or React throws away the server HTML.
  const isHydrated = useIsHydrated();
  const storeSelected = activeTab === Config.ROUTES.promotionpage.store;
  const ecommerceSelected = activeTab === Config.ROUTES.promotionpage.ecommerce;

  const offline = usePromotionOfferList(false, selectedOfflineStore, storeSelected ? 'butik' : undefined);
  const online = usePromotionOfferList(false, selectedOfflineStore, ecommerceSelected ? 'ehandel' : undefined);

  const { combinedGeneralCampaigns, personalCampaigns, isLoading, error, hasMore, loadMore } = storeSelected
    ? offline
    : online;

  const { cmsPage } = useCmsPage(label);

  useEffect(() => {
    if (activeTab === Config.ROUTES.promotionpage.store) {
      trackOfflineStoreState(selectedOfflineStore?.storeId as string);
    }
  }, [activeTab, selectedOfflineStore]);

  const cmsComponents = useMemo(() => {
    if (!cmsPage?.slots || storeSelected) return [];
    const mainContentSlot = cmsPage.slots.find((slot: any) => slot.position === 'MainContent');
    return mainContentSlot?.components?.component || [];
  }, [cmsPage, storeSelected]);

  useEffect(() => {
    if (!promotionStoreIdLocalStorage) {
      setPromotionStoreIdLocalStorage(customer?.homeStoreId || '');
    }
  }, [customer?.isAnonymous]);

  const shouldShowEmptyState =
    storeSelected &&
    !offline.isLoading &&
    !offline.combinedGeneralCampaigns?.results?.length &&
    !offline.personalCampaigns;

  const showSkeleton = !isHydrated || (isLoading && !combinedGeneralCampaigns?.results?.length);

  const [infiniteRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage: hasMore,
    onLoadMore: () => loadMore(false),
    rootMargin: '0px 0px 400px 0px',
  });

  const renderPromotionOffersFragment = (storeType: onlineOfflineType) => (
    <>
      <PromotionOffersFragment
        storeType={storeType}
        generalCampaigns={combinedGeneralCampaigns}
        personalCampaigns={personalCampaigns}
        isLoading={isLoading}
        cmsComponents={cmsComponents}
      />
      {hasMore && <Spinner size={32} ref={infiniteRef} />}
    </>
  );
  if (!isLoading && error) return <ErrorComponent errorCode={error?.status} />;

  return (
    <Page cmsPage={cmsPage}>
      <PageLayout>
        <StyledPromotionPageContent>
          <StyledPromotionPageHeading variant="h1">{t('heading')}</StyledPromotionPageHeading>
          <PromotionTabs
            activeTab={activeTab}
            ecommerceSelected={ecommerceSelected}
            setSelectedOfflineStore={setSelectedOfflineStore}
            selectedOfflineStore={isHydrated ? selectedOfflineStore : NO_STORE}
            shouldOpenFlyer={shouldOpenFlyer}
          />

          {isHydrated && (
            <>
              {storeSelected && !!selectedOfflineStore?.storeId && !selectedOfflineStore?.open && (
                <StyledClosedStoreAlert>
                  <Alert id="storeNotOpenYet" hasCloseButton={false} $theme="info">
                    <Text type="body" size="small">
                      {t('storeNotOpenYet')}
                    </Text>
                  </Alert>
                </StyledClosedStoreAlert>
              )}

              {storeSelected && selectedOfflineStore?.open && renderPromotionOffersFragment('offline')}

              {shouldShowEmptyState && (
                <IconWrapper>
                  <Icon svg={EmptyBag} size={isMobile ? 240 : 320} data-testid="empty-cart-icon" />
                  <Text type="body" size="medium">
                    {t('selectStore')}
                  </Text>
                </IconWrapper>
              )}

              {ecommerceSelected && store?.storeId && renderPromotionOffersFragment('online')}
            </>
          )}

          {showSkeleton && <ProductBeamSkeleton type="promotionBeam" offlineProduct={mode === 'butik'} rows={2} />}
        </StyledPromotionPageContent>
      </PageLayout>
    </Page>
  );
};

export default PromotionPageClient;
